import { ArrowDownward, Close } from "@mui/icons-material";
import {
  Button,
  Card,
  CardContent,
  Divider,
  Grid,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";

import { DisponibilityTab } from "./DisponibilityTab";
import { LocationInfo } from "./LocationInfo";
import { PractitionersOptions } from "./PractitionersOptions";

import { isMobile } from "react-device-detect";
import { DataSkeleton } from "../../../components/common";
import { useOrganizationContext } from "../../../contexts/OrganizationContext";
import { GetSlot } from "../../../services/fhir";
import { useFhirData } from "../../../utils/useFhirData";
import { SchedulingScreenMobile } from "../../schedulingscreenmobile/SchedulingScreenMobile";
import { DisponibilityCardMobile } from "./DisponibilityCardMobile";

interface IProps {
  location: fhir5.Location;
  schedules: any;
  startDate: string;
  hasAttendedByTargetOrg: boolean;
}

// Helper para extrair ID limpo de referências FHIR
const getSimpleId = (idOrRef: string | undefined): string | undefined => {
  if (!idOrRef) return undefined;
  return idOrRef.includes("/") ? idOrRef.split("/").pop() : idOrRef;
};

/**
 * Dedup de profissionais por chave estável.
 *
 * Motivação: alguns cenários retornam múltiplos `Schedule` para o mesmo `Practitioner`.
 * Se montarmos a lista 1:1 com schedules, o paginador pode "aumentar" a lista ao
 * navegar (percepção de duplicação). A deduplicação aqui é a mudança mínima e local
 * que garante lista determinística sem alterar regras de filtros/busca.
 */
const getPractitionerKey = (practitioner: any): string | undefined => {
  // Preferimos `reference` (padrão FHIR "Practitioner/{id}") e caímos no `id` se necessário.
  return (
    getSimpleId(practitioner?.reference) ??
    getSimpleId(practitioner?.id) ??
    undefined
  );
};

export function DisponibilityCard({
  location,
  startDate,
  schedules,
  hasAttendedByTargetOrg,
}: IProps) {
  const isExtraLargeScreen = useMediaQuery("(min-width:1199px)");
  const [open, setOpen] = useState<boolean>(
    hasAttendedByTargetOrg ? true : false,
  );
  const [selectedPractitioner, setSelectedPractitioner] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [slots, setSlots] = useState<fhir5.BundleEntry<fhir5.Slot>[]>();
  const [scheduleCompleted, setScheduleCompleted] = useState<any>();
  const [slotType, setSlotType] = useState();
  const [slotsLoading, setSlotsLoading] = useState(false);

  const [refetchSlots, setRefetchSlots] = useState<boolean>(false);
  const [openSchedulingScreen, setOpenSchedulingScreen] = useState<boolean>(
    hasAttendedByTargetOrg && isMobile ? true : false,
  );

  // Mapeia os atores (profissionais) dos agendamentos
  const actors = useMemo(
    () =>
      (() => {
        const mapped =
          schedules?.map((schedule: any) => {
            const practitioner = useFhirData.findActor(
              schedule?.resource?.actor,
              "Practitioner",
            );
            const healthcare = useFhirData.findActor(
              schedule?.resource?.actor,
              "Healthcare",
            );

            // Extrai o custo do serviço se disponível na extensão
            const costExtension = schedule?.resource?.extension?.find(
              (ext: any) =>
                ext.url ===
                  "http://www.saude.gov.br/fhir/r4/StructureDefinition/BRServiceCost" ||
                ext.url?.includes("service-cost"),
            );
            const cost = costExtension?.valueDecimal
              ? Number(costExtension.valueDecimal)
              : 0;

            return {
              ...practitioner,
              healthcare,
              serviceCost: cost,
              originalSchedule: schedule,
            };
          }) || [];

        // Dedup preservando a primeira ocorrência (mantém comportamento o mais próximo possível).
        const seen = new Set<string>();
        return mapped.filter((p: any) => {
          const key = getPractitionerKey(p);
          if (!key) return true; // fail-open: se não houver chave, não filtramos
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });
      })(),
    [schedules],
  );

  const { organizationList } = useOrganizationContext();
  const findOrganization = organizationList?.find(
    (org) =>
      org?.resource?.id ===
      location?.managingOrganization?.reference?.split("/")?.[1],
  );

  // Busca slots APENAS para o profissional selecionado (comportamento original seguro)
  useEffect(() => {
    async function fetchSlots() {
      // Se não estiver aberto nem no desktop nem no mobile, não busca
      if (!open && !openSchedulingScreen) return;

      const selectedActor = actors[selectedPractitioner];
      if (!selectedActor) {
        setSlots([]);
        return;
      }

      // Prepara dados do selecionado
      setSlotType(selectedActor.healthcare);
      setScheduleCompleted(selectedActor.originalSchedule);

      const scheduleId = getSimpleId(
        selectedActor.originalSchedule?.resource?.id,
      );

      if (!scheduleId) {
        setSlots([]);
        return;
      }

      setSlotsLoading(true);
      try {
        const response = await GetSlot({
          schedule: scheduleId,
          startDate,
          status: ["free"],
          scheduleActive: true,
        });
        setSlots(response?.entry ?? []);
      } catch (err) {
        console.error("Erro ao buscar slots:", err);
        setSlots([]);
      } finally {
        setSlotsLoading(false);
      }
    }

    fetchSlots();
  }, [open, openSchedulingScreen, startDate, selectedPractitioner, actors]);

  return (
    <>
      {isMobile ? (
        <DisponibilityCardMobile
          location={location}
          onClick={() => setOpenSchedulingScreen(true)}
        />
      ) : (
        <Card
          sx={{
            width: "100%",
            height: "fit-content",
            marginTop: 2,
            border: "1px solid",
            borderColor: "primary700.main",
            position: "relative",
          }}
        >
          {open && (
            <IconButton
              sx={{
                width: "fit-content",
                position: "absolute",
                top: "4px",
                right: "4px",
                padding: "6px",
              }}
              onClick={() => setOpen(false)}
            >
              <Close fontSize="small" />
            </IconButton>
          )}

          <CardContent sx={{ padding: "16px !important" }}>
            <Grid container height="fit-content" justifyContent="center" mt={0}>
              <Grid
                item
                xs={12}
                lg={5.8}
                sx={{
                  height: "100%",
                  paddingRight: { lg: 3 }, // Adiciona padding à direita para afastar do divider
                  overflow: "hidden",
                }}
              >
                <Stack>
                  <Stack sx={{ flexDirection: "row", justifyContent: "left" }}>
                    <LocationInfo
                      location={location}
                      organization={findOrganization}
                    />
                  </Stack>
                  {open ? (
                    <PractitionersOptions
                      slotType={slotType}
                      organization={findOrganization}
                      practitioners={actors}
                      selectedPractitioner={selectedPractitioner}
                      setSelectedPractitioner={setSelectedPractitioner}
                    />
                  ) : (
                    <></>
                  )}
                </Stack>
              </Grid>

              <Grid
                item
                xs={12}
                lg={0.4}
                display="flex"
                justifyContent="center"
              >
                <Divider
                  orientation={isExtraLargeScreen ? "vertical" : "horizontal"}
                  sx={{
                    marginTop: `${!isExtraLargeScreen && "6px"}`,
                    marginBottom: `${!isExtraLargeScreen && "6px"}`,
                    // Remove margens laterais manuais para deixar o Grid controlar
                    backgroundColor: "primary700.main",
                    height: `${isExtraLargeScreen ? "100%" : "1px"}`,
                    width: `${isExtraLargeScreen ? "1px" : "100%"}`,
                  }}
                />
              </Grid>

              <Grid
                item
                xs={12}
                lg={5.8}
                sx={{
                  justifyContent: "center",
                  alignItems: "center",
                  width: "100%",
                  alignContent: "center",
                  paddingLeft: { lg: 3 }, // Adiciona padding à esquerda para simetria
                }}
              >
                {!open ? (
                  <Button
                    sx={{
                      width: "100%",
                      textTransform: "none",
                      "&:hover": {
                        background: "none",
                      },
                      height: "100%",
                      minHeight: "70px",
                    }}
                    onClick={() => setOpen(true)}
                  >
                    <Typography
                      variant="body2"
                      fontWeight="bold"
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "6px",
                        color: "primary700.main",
                      }}
                    >
                      Checar disponibilidade
                      <ArrowDownward fontSize="small" />
                    </Typography>
                  </Button>
                ) : !loading ? (
                  <DisponibilityTab
                    slots={slots}
                    loading={slotsLoading}
                    location={location}
                    setRefetchSlots={setRefetchSlots}
                    scheduleCompleted={scheduleCompleted}
                  />
                ) : (
                  <DataSkeleton />
                )}
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}
      <SchedulingScreenMobile
        open={openSchedulingScreen}
        onClose={() => setOpenSchedulingScreen(false)}
        location={location}
        practitioners={actors}
        slots={slots}
        scheduleCompleted={scheduleCompleted}
        loading={loading}
        setRefetchSlots={setRefetchSlots}
        selectedPractitioner={selectedPractitioner}
        setSelectedPractitioner={setSelectedPractitioner}
        startDate={startDate}
      />
    </>
  );
}
